import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { User } from 'src/app/shared/model/user';
import { CallAudioService } from './call-audio.service';
import { RoomService } from './room.service';

@Injectable({
  providedIn: 'root',
})
export class CallService {
  showModal = false;
  mode: 'incoming' | 'outgoing' | 'active' = 'incoming';
  caller: User | null = null;
  currentCallId: number | null = null;
  isLoadingCall = false;
  isAccepting = false;
  isDeclining = false;
  elapsed = '00:00';

  private stateChanged = new Subject<void>();
  stateChanged$ = this.stateChanged.asObservable();

  private currentUserId?: number;
  private startedAt: Date | null = null;
  private timer?: ReturnType<typeof setInterval>;
  private baseUrl = environment.api.basePath || 'https://localhost:44363/API';

  constructor(
    private http: HttpClient,
    private roomService: RoomService,
    private callAudio: CallAudioService,
    private zone: NgZone,
    store: Store<{ user: { currentUser?: { id: number } } }>
  ) {
    store.select((s) => s.user.currentUser?.id).subscribe((id) => (this.currentUserId = id));

    this.roomService.callInitiated$.subscribe((call) => this.zone.run(() => this.onIncoming(call)));
    this.roomService.callAccepted$.subscribe((call) => this.zone.run(() => this.onAccepted(call)));
    this.roomService.callDeclined$.subscribe((call) => this.zone.run(() => this.onDeclined(call)));
    this.roomService.callEnded$.subscribe((call) => this.zone.run(() => this.onEnded(call)));
  }

  initiateCall(recipient: User): void {
    if (!recipient?.id || this.isLoadingCall) return;

    this.isLoadingCall = true;
    this.stateChanged.next();
    this.http.post<any>(`${this.baseUrl}/Call/Initiate`, null, {
      params: { recipientId: recipient.id }
    }).subscribe({
      next: (call) => {
        this.currentCallId = call.id;
        this.caller = recipient;
        this.mode = 'outgoing';
        this.showModal = true;
        this.isLoadingCall = false;
        this.stateChanged.next();
      },
      error: (err) => {
        console.error('Failed to initiate call:', err);
        this.isLoadingCall = false;
        this.stateChanged.next();
      },
    });
  }

  acceptCall(): void {
    if (!this.currentCallId || this.isAccepting) return;

    this.isAccepting = true;
    this.http.post<any>(`${this.baseUrl}/Call/Accept`, null, {
      params: { callId: this.currentCallId }
    }).subscribe({
      next: (call) => {
        this.callAudio.start(call.id, call.fromUserId, 'receiver')
          .then(() => this.roomService.sendCallSignal(call.id, call.fromUserId, 'ready', ''))
          .catch((err) => console.error('Failed to start call audio:', err));
        this.isAccepting = false;
        this.startInCall(call.startedAt);
      },
      error: (err) => {
        console.error('Failed to accept call:', err);
        this.isAccepting = false;
      },
    });
  }

  declineCall(): void {
    if (!this.currentCallId || this.isDeclining) return;

    this.isDeclining = true;
    this.http.post<any>(`${this.baseUrl}/Call/Decline`, null, {
      params: { callId: this.currentCallId }
    }).subscribe({
      next: () => {
        this.isDeclining = false;
        this.showModal = false;
      },
      error: (err) => {
        console.error('Failed to decline call:', err);
        this.isDeclining = false;
      },
    });
  }

  cancelCall(): void {
    if (!this.currentCallId) return;

    this.http.post(`${this.baseUrl}/Call/Cancel`, null, {
      params: { callId: this.currentCallId }
    }).subscribe({
      error: (err) => console.error('Failed to cancel call:', err),
    });
  }

  endCall(): void {
    if (!this.currentCallId) return;

    this.http.post(`${this.baseUrl}/Call/End`, null, {
      params: { callId: this.currentCallId }
    }).subscribe({
      error: (err) => console.error('Failed to end call:', err),
    });
  }

  private onIncoming(call: { id: number; fromUserId: number; toUserId: number; fromUser: Partial<User> }): void {
    if (call.toUserId === this.currentUserId && call.fromUserId !== this.currentUserId) {
      this.currentCallId = call.id;
      this.caller = call.fromUser as User;
      this.mode = 'incoming';
      this.showModal = true;
    }
  }

  private onAccepted(call: { id: number; fromUserId: number; toUserId: number; startedAt: string }): void {
    if (call.id === this.currentCallId && this.mode === 'outgoing') {
      this.callAudio.start(call.id, call.toUserId, 'caller')
        .catch((err) => console.error('Failed to start call audio:', err));
      this.startInCall(call.startedAt);
    }
  }

  private onDeclined(call: { id: number }): void {
    if (call.id === this.currentCallId) {
      this.currentCallId = null;
      this.showModal = false;
    }
  }

  private onEnded(call: { id: number }): void {
    if (call.id === this.currentCallId) {
      this.stopInCall();
    }
  }

  private startInCall(startedAt: string): void {
    this.mode = 'active';
    this.showModal = true;
    this.startedAt = new Date(startedAt);
    this.updateElapsed();
    clearInterval(this.timer);
    this.timer = setInterval(() => this.updateElapsed(), 1000);
  }

  private stopInCall(): void {
    this.callAudio.stop();
    clearInterval(this.timer);
    this.timer = undefined;
    this.startedAt = null;
    this.elapsed = '00:00';
    this.currentCallId = null;
    this.showModal = false;
  }

  private updateElapsed(): void {
    if (!this.startedAt) return;
    const seconds = Math.floor((Date.now() - this.startedAt.getTime()) / 1000);
    const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
    const ss = String(seconds % 60).padStart(2, '0');
    this.elapsed = `${mm}:${ss}`;
  }
}
